/**
 * DCMCloud — Multi-Monitor Controls Component
 * Toolbar / panel widget for managing secondary monitor windows.
 * Uses BroadcastChannel API for real-time WW/WL, scroll, and viewport sync.
 *
 * Usage: add <MultiMonitorControls servicesManager={...} /> to the viewer header.
 * The BroadcastChannel listener is set up automatically on mount.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';

const CHANNEL_NAME = 'dcmcloud-multimonitor-sync';

export type SyncMessage =
  | { type: 'WW_WL'; viewportId: string; windowWidth: number; windowCenter: number }
  | { type: 'SCROLL'; viewportId: string; frameIndex: number }
  | { type: 'TOOL_ACTIVE'; toolName: string }
  | { type: 'PING'; fromScreen: number }
  | { type: 'PONG'; fromScreen: number; windowCount: number };

interface Props {
  servicesManager?: any;
}

/** Shared broadcast channel — singleton per tab */
let _channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!_channel) _channel = new BroadcastChannel(CHANNEL_NAME);
  return _channel;
}

/** Post a sync message to all other DCMCloud tabs/windows */
export function dcmcloudPostSync(msg: SyncMessage) {
  getChannel()?.postMessage(msg);
}

export function MultiMonitorControls({ servicesManager }: Props) {
  const [screenCount, setScreenCount] = useState(1);
  const [connectedWindows, setConnectedWindows] = useState<number[]>([]);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const pingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect how many physical screens the OS reports
  useEffect(() => {
    const win = window as any;
    if (win.getScreenDetails) {
      win.getScreenDetails().then((sd: any) => setScreenCount(sd.screens?.length ?? 1)).catch(() => {});
    } else if (win.screen?.isExtended) {
      setScreenCount(2);
    }
  }, []);

  // BroadcastChannel listener: receive sync messages from other windows
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;

    const handler = (event: MessageEvent<SyncMessage>) => {
      const msg = event.data;
      if (!msg?.type) return;

      if (msg.type === 'PING') {
        ch.postMessage({ type: 'PONG', fromScreen: window.__dcmcloud_screenNumber ?? -1, windowCount: 1 } as SyncMessage);
        setConnectedWindows(prev => Array.from(new Set([...prev, msg.fromScreen])));
        return;
      }
      if (msg.type === 'PONG') {
        setConnectedWindows(prev => Array.from(new Set([...prev, msg.fromScreen])));
        return;
      }

      if (!syncEnabled || !servicesManager) return;
      const { viewportGridService, cornerstoneViewportService } = servicesManager.services ?? {};

      if (msg.type === 'WW_WL' && cornerstoneViewportService) {
        try {
          const vp = cornerstoneViewportService.getCornerstoneViewport(msg.viewportId);
          if (vp) {
            vp.setProperties({ voiRange: { lower: msg.windowCenter - msg.windowWidth / 2, upper: msg.windowCenter + msg.windowWidth / 2 } });
            vp.render();
          }
        } catch {}
      }

      if (msg.type === 'SCROLL' && cornerstoneViewportService) {
        try {
          const vp = cornerstoneViewportService.getCornerstoneViewport(msg.viewportId);
          if (vp && 'setImageIdIndex' in vp) {
            (vp as any).setImageIdIndex(msg.frameIndex);
            vp.render();
          }
        } catch {}
      }
    };

    ch.addEventListener('message', handler);
    return () => ch.removeEventListener('message', handler);
  }, [syncEnabled, servicesManager]);

  // Ping other windows on mount + every 30s
  useEffect(() => {
    const ping = () => {
      const ch = getChannel();
      if (ch) ch.postMessage({ type: 'PING', fromScreen: (window as any).__dcmcloud_screenNumber ?? -1 } as SyncMessage);
    };
    ping();
    const t = setInterval(ping, 30_000);
    return () => clearInterval(t);
  }, []);

  const openSecondaryWindow = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('multimonitor', 'split');
    const w = window.screen?.width ?? 1920;
    const h = window.screen?.height ?? 1080;
    window.open(url.toString(), `dcmcloud-monitor-${Date.now()}`, `width=${Math.round(w * 0.5)},height=${h},left=${Math.round(w * 0.5)},top=0,menubar=no,toolbar=no,status=no`);
  }, []);

  const totalConnected = connectedWindows.length;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', background: '#111827', borderRadius: 6, border: '1px solid #1f2937', userSelect: 'none' }}>
      {/* Monitor icon + count */}
      <span style={{ fontSize: '14px' }} title={`${screenCount} screen(s) detected`}>🖥</span>
      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
        {totalConnected > 0 ? (
          <span style={{ color: '#10b981' }}>{totalConnected + 1} windows linked</span>
        ) : (
          <span>1 window</span>
        )}
      </span>

      {/* Sync toggle */}
      <button
        onClick={() => setSyncEnabled(s => !s)}
        title={syncEnabled ? 'Sync ON — click to disable' : 'Sync OFF — click to enable'}
        style={{ padding: '2px 8px', borderRadius: 4, border: `1px solid ${syncEnabled ? '#10b981' : '#4b5563'}`, background: syncEnabled ? '#10b98122' : 'transparent', color: syncEnabled ? '#10b981' : '#6b7280', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
      >
        {syncEnabled ? '⇄ Sync ON' : '⇄ Sync OFF'}
      </button>

      {/* Open secondary window */}
      <button
        onClick={openSecondaryWindow}
        title="Open study on second monitor"
        style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #3b82f6', background: '#3b82f622', color: '#3b82f6', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
      >
        + Monitor
      </button>
    </div>
  );
}

export default MultiMonitorControls;
